import { resolveReasoningSummarySupport } from './reasoning.js';
import type { AIModel, Provider } from './types.js';

/**
 * Static catalogue used to seed the database and as a fallback when a provider
 * `/models` endpoint is unreachable. Prices are USD list prices and are meant to
 * be refreshed by the model-sync cron job or edited by an admin; they are the
 * *provider* cost, the user price is derived via FX rate + markup.
 */

export const PROVIDER_CATALOG: Provider[] = [
  {
    id: 'openai',
    code: 'openai',
    displayName: 'OpenAI',
    baseUrl: 'https://api.openai.com/v1',
    isActive: true,
    hasApi: true,
    websiteUrl: 'https://openai.com',
  },
  {
    id: 'anthropic',
    code: 'anthropic',
    displayName: 'Anthropic',
    baseUrl: 'https://api.anthropic.com/v1',
    isActive: true,
    hasApi: true,
    websiteUrl: 'https://anthropic.com',
  },
  {
    id: 'google',
    code: 'google',
    displayName: 'Google',
    baseUrl: 'https://generativelanguage.googleapis.com/v1beta',
    isActive: true,
    hasApi: true,
    websiteUrl: 'https://ai.google.dev',
  },
  {
    id: 'xai',
    code: 'xai',
    displayName: 'xAI (Grok)',
    baseUrl: 'https://api.x.ai/v1',
    isActive: true,
    hasApi: true,
    websiteUrl: 'https://x.ai',
  },
  {
    id: 'deepseek',
    code: 'deepseek',
    displayName: 'DeepSeek',
    baseUrl: 'https://api.deepseek.com/v1',
    isActive: true,
    hasApi: true,
    websiteUrl: 'https://deepseek.com',
  },
  {
    id: 'moonshot',
    code: 'moonshot',
    displayName: 'Moonshot (Kimi)',
    baseUrl: 'https://api.moonshot.ai/v1',
    isActive: true,
    hasApi: true,
    websiteUrl: 'https://kimi.moonshot.cn',
  },
  {
    id: 'microsoft',
    code: 'microsoft',
    displayName: 'Microsoft (MAI)',
    baseUrl: 'https://api.microsoft.com/mai/v1',
    isActive: true,
    hasApi: true,
    websiteUrl: 'https://microsoft.ai',
  },
  {
    id: 'elevenlabs',
    code: 'elevenlabs',
    displayName: 'ElevenLabs',
    baseUrl: 'https://api.elevenlabs.io/v1',
    isActive: true,
    hasApi: true,
    websiteUrl: 'https://elevenlabs.io',
  },
  {
    id: 'suno',
    code: 'suno',
    displayName: 'Suno',
    baseUrl: 'https://api.suno.ai/v1',
    isActive: true,
    hasApi: true,
    websiteUrl: 'https://suno.com',
  },
  {
    id: 'udio',
    code: 'udio',
    displayName: 'Udio',
    baseUrl: 'https://api.udio.com/v1',
    isActive: true,
    hasApi: true,
    websiteUrl: 'https://udio.com',
  },
  {
    id: 'aiva',
    code: 'aiva',
    displayName: 'AIVA',
    baseUrl: 'https://api.aiva.ai/v1',
    isActive: true,
    hasApi: true,
    websiteUrl: 'https://aiva.ai',
  },
  {
    id: 'kling',
    code: 'kling',
    displayName: 'Kling AI',
    baseUrl: 'https://api.klingai.com/v1',
    isActive: true,
    hasApi: true,
    websiteUrl: 'https://klingai.com',
  },
  {
    id: 'higgsfield',
    code: 'higgsfield',
    displayName: 'Higgsfield',
    baseUrl: 'https://api.higgsfield.ai',
    isActive: true,
    hasApi: true,
    websiteUrl: 'https://higgsfield.ai',
  },
  {
    id: 'meshy',
    code: 'meshy',
    displayName: 'Meshy (3D)',
    baseUrl: 'https://api.meshy.ai/openapi/v1',
    isActive: true,
    hasApi: true,
    websiteUrl: 'https://www.meshy.ai',
  },
  {
    id: 'runway',
    code: 'runway',
    displayName: 'Runway',
    baseUrl: 'https://api.dev.runwayml.com/v1',
    isActive: true,
    hasApi: true,
    websiteUrl: 'https://runwayml.com',
  },
  {
    id: 'luma',
    code: 'luma',
    displayName: 'Luma AI',
    baseUrl: 'https://api.lumalabs.ai/dream-machine/v1',
    isActive: true,
    hasApi: true,
    websiteUrl: 'https://lumalabs.ai',
  },
  {
    id: 'minimax',
    code: 'minimax',
    displayName: 'MiniMax (Hailuo)',
    baseUrl: 'https://api.minimax.chat/v1',
    isActive: true,
    hasApi: true,
    websiteUrl: 'https://hailuoai.video',
  },
  {
    id: 'bytedance',
    code: 'bytedance',
    displayName: 'ByteDance (Seedream)',
    baseUrl: 'https://ark.cn-beijing.volces.com/api/v3',
    isActive: true,
    hasApi: true,
    websiteUrl: 'https://www.volcengine.com',
  },
  {
    id: 'bfl',
    code: 'bfl',
    displayName: 'Black Forest Labs (FLUX)',
    baseUrl: 'https://api.bfl.ai',
    isActive: true,
    hasApi: true,
    websiteUrl: 'https://bfl.ai',
  },
  {
    id: 'hume',
    code: 'hume',
    displayName: 'Hume AI',
    baseUrl: 'https://api.hume.ai/v0',
    isActive: true,
    hasApi: true,
    websiteUrl: 'https://hume.ai',
  },
  {
    id: 'murf',
    code: 'murf',
    displayName: 'Murf AI',
    baseUrl: 'https://api.murf.ai/v1',
    isActive: true,
    hasApi: true,
    websiteUrl: 'https://murf.ai',
  },
  {
    id: 'design-tools',
    code: 'design-tools',
    displayName: 'Design tools',
    baseUrl: '',
    isActive: true,
    hasApi: false,
    websiteUrl: null,
  },
  {
    id: 'qwen',
    code: 'qwen',
    displayName: 'Qwen (Alibaba)',
    baseUrl: 'https://dashscope.aliyuncs.com/api/v1',
    isActive: true,
    hasApi: true,
    websiteUrl: 'https://qwenlm.github.io',
  },
  {
    id: 'reve',
    code: 'reve',
    displayName: 'Reve AI',
    baseUrl: 'https://api.reve.art/v1',
    isActive: true,
    hasApi: true,
    websiteUrl: 'https://reve.art',
  },
];

type ModelSeed = Omit<AIModel, 'id' | 'lastSeenAt' | 'priceConfirmedAt'> & {
  lastSeenAt?: string | null;
  priceConfirmedAt?: string | null;
};

export function modelId(providerCode: string, modelCode: string): string {
  return `${providerCode}:${modelCode}`;
}

const seeds: ModelSeed[] = [
  // ───────────────────────── 1. Chat / text ─────────────────────────
  {
    providerCode: 'openai',
    modelCode: 'gpt-4.1',
    displayName: 'GPT-4.1',
    section: 'chat',
    modality: 'text',
    pricingUnit: 'tokens',
    contextLimit: 1_047_576,
    maxOutputTokens: 32_768,
    supportsStreaming: true,
    supportsVision: true,
    supportsWebSearch: true,
    isActive: true,
    isAvailable: true,
    price: { inputPer1kUsd: 0.002, outputPer1kUsd: 0.008 },
  },
  {
    providerCode: 'openai',
    modelCode: 'gpt-4.1-mini',
    displayName: 'GPT-4.1 mini',
    section: 'chat',
    modality: 'text',
    pricingUnit: 'tokens',
    contextLimit: 1_047_576,
    maxOutputTokens: 32_768,
    supportsStreaming: true,
    supportsVision: true,
    supportsWebSearch: true,
    isActive: true,
    isAvailable: true,
    price: { inputPer1kUsd: 0.0004, outputPer1kUsd: 0.0016 },
  },
  {
    providerCode: 'openai',
    modelCode: 'gpt-4o',
    displayName: 'GPT-4o',
    section: 'chat',
    modality: 'text',
    pricingUnit: 'tokens',
    contextLimit: 128_000,
    maxOutputTokens: 16_384,
    supportsStreaming: true,
    supportsVision: true,
    supportsWebSearch: true,
    isActive: true,
    isAvailable: true,
    price: { inputPer1kUsd: 0.0025, outputPer1kUsd: 0.01 },
  },
  {
    providerCode: 'openai',
    modelCode: 'o3-mini',
    displayName: 'o3-mini (reasoning)',
    section: 'chat',
    modality: 'text',
    pricingUnit: 'tokens',
    contextLimit: 200_000,
    maxOutputTokens: 100_000,
    supportsStreaming: true,
    supportsVision: false,
    isActive: true,
    isAvailable: true,
    price: { inputPer1kUsd: 0.0011, outputPer1kUsd: 0.0044 },
  },
  {
    providerCode: 'anthropic',
    modelCode: 'claude-sonnet-4-5',
    displayName: 'Claude Sonnet 4.5',
    section: 'chat',
    modality: 'text',
    pricingUnit: 'tokens',
    contextLimit: 200_000,
    maxOutputTokens: 64_000,
    supportsStreaming: true,
    supportsVision: true,
    isActive: true,
    isAvailable: true,
    price: { inputPer1kUsd: 0.003, outputPer1kUsd: 0.015 },
  },
  {
    providerCode: 'anthropic',
    modelCode: 'claude-opus-4-1',
    displayName: 'Claude Opus 4.1',
    section: 'chat',
    modality: 'text',
    pricingUnit: 'tokens',
    contextLimit: 200_000,
    maxOutputTokens: 32_000,
    supportsStreaming: true,
    supportsVision: true,
    isActive: true,
    isAvailable: true,
    price: { inputPer1kUsd: 0.015, outputPer1kUsd: 0.075 },
  },
  {
    providerCode: 'anthropic',
    modelCode: 'claude-haiku-4-5',
    displayName: 'Claude Haiku 4.5',
    section: 'chat',
    modality: 'text',
    pricingUnit: 'tokens',
    contextLimit: 200_000,
    maxOutputTokens: 64_000,
    supportsStreaming: true,
    supportsVision: true,
    isActive: true,
    isAvailable: true,
    price: { inputPer1kUsd: 0.001, outputPer1kUsd: 0.005 },
  },
  {
    providerCode: 'google',
    modelCode: 'gemini-2.5-pro',
    displayName: 'Gemini 2.5 Pro',
    section: 'chat',
    modality: 'text',
    pricingUnit: 'tokens',
    contextLimit: 1_048_576,
    maxOutputTokens: 65_536,
    supportsStreaming: true,
    supportsVision: true,
    supportsWebSearch: true,
    isActive: true,
    isAvailable: true,
    price: { inputPer1kUsd: 0.00125, outputPer1kUsd: 0.01 },
  },
  {
    providerCode: 'google',
    modelCode: 'gemini-2.5-flash',
    displayName: 'Gemini 2.5 Flash',
    section: 'chat',
    modality: 'text',
    pricingUnit: 'tokens',
    contextLimit: 1_048_576,
    maxOutputTokens: 65_536,
    supportsStreaming: true,
    supportsVision: true,
    supportsWebSearch: true,
    isActive: true,
    isAvailable: true,
    price: { inputPer1kUsd: 0.0003, outputPer1kUsd: 0.0025 },
  },
  {
    providerCode: 'xai',
    modelCode: 'grok-4',
    displayName: 'Grok 4',
    section: 'chat',
    modality: 'text',
    pricingUnit: 'tokens',
    contextLimit: 256_000,
    maxOutputTokens: 32_000,
    supportsStreaming: true,
    supportsVision: true,
    supportsWebSearch: true,
    isActive: true,
    isAvailable: true,
    price: { inputPer1kUsd: 0.003, outputPer1kUsd: 0.015 },
  },
  {
    providerCode: 'xai',
    modelCode: 'grok-3-mini',
    displayName: 'Grok 3 mini',
    section: 'chat',
    modality: 'text',
    pricingUnit: 'tokens',
    contextLimit: 131_072,
    maxOutputTokens: 16_384,
    supportsStreaming: true,
    supportsVision: false,
    supportsWebSearch: true,
    isActive: true,
    isAvailable: true,
    price: { inputPer1kUsd: 0.0003, outputPer1kUsd: 0.0005 },
  },
  {
    providerCode: 'deepseek',
    modelCode: 'deepseek-chat',
    displayName: 'DeepSeek V3 Chat',
    section: 'chat',
    modality: 'text',
    pricingUnit: 'tokens',
    contextLimit: 128_000,
    maxOutputTokens: 8_192,
    supportsStreaming: true,
    supportsVision: false,
    isActive: true,
    isAvailable: true,
    price: { inputPer1kUsd: 0.00027, outputPer1kUsd: 0.0011 },
  },
  {
    providerCode: 'deepseek',
    modelCode: 'deepseek-reasoner',
    displayName: 'DeepSeek R1 Reasoner',
    section: 'chat',
    modality: 'text',
    pricingUnit: 'tokens',
    contextLimit: 128_000,
    maxOutputTokens: 65_536,
    supportsStreaming: true,
    supportsVision: false,
    isActive: true,
    isAvailable: true,
    price: { inputPer1kUsd: 0.00055, outputPer1kUsd: 0.00219 },
  },
  {
    providerCode: 'moonshot',
    modelCode: 'kimi-k2-0905-preview',
    displayName: 'Kimi K2',
    section: 'chat',
    modality: 'text',
    pricingUnit: 'tokens',
    contextLimit: 256_000,
    maxOutputTokens: 16_384,
    supportsStreaming: true,
    supportsVision: false,
    isActive: true,
    isAvailable: true,
    price: { inputPer1kUsd: 0.0006, outputPer1kUsd: 0.0025 },
  },
  {
    providerCode: 'microsoft',
    modelCode: 'mai-1-preview',
    displayName: 'MAI-1 (Microsoft)',
    section: 'chat',
    modality: 'text',
    pricingUnit: 'tokens',
    contextLimit: 128_000,
    maxOutputTokens: 16_384,
    supportsStreaming: true,
    supportsVision: false,
    isActive: true,
    isAvailable: true,
    price: { inputPer1kUsd: 0.001, outputPer1kUsd: 0.004 },
  },

  // ───────────────────────── 2. Images ─────────────────────────
  // Nano Banana Pro
  {
    providerCode: 'google',
    modelCode: 'gemini-2.5-flash-image',
    displayName: 'Nano Banana Pro',
    section: 'image',
    modality: 'image',
    pricingUnit: 'image',
    contextLimit: 32_000,
    maxOutputTokens: 0,
    supportsStreaming: false,
    supportsVision: true,
    isActive: true,
    isAvailable: true,
    price: { inputPer1kUsd: 0.0003, outputPer1kUsd: 0, perImageUsd: 0.039 },
  },
  // Nano Banana 2 Lite
  {
    providerCode: 'google',
    modelCode: 'gemini-2.5-flash-image-lite',
    displayName: 'Nano Banana 2 Lite',
    section: 'image',
    modality: 'image',
    pricingUnit: 'image',
    contextLimit: 32_000,
    maxOutputTokens: 0,
    supportsStreaming: false,
    supportsVision: true,
    isActive: true,
    isAvailable: true,
    price: { inputPer1kUsd: 0.0002, outputPer1kUsd: 0, perImageUsd: 0.02 },
  },
  // Nano Banana
  {
    providerCode: 'google',
    modelCode: 'gemini-3-pro-image',
    displayName: 'Nano Banana',
    section: 'image',
    modality: 'image',
    pricingUnit: 'image',
    contextLimit: 32_000,
    maxOutputTokens: 0,
    supportsStreaming: false,
    supportsVision: true,
    isActive: true,
    isAvailable: true,
    price: { inputPer1kUsd: 0.0005, outputPer1kUsd: 0, perImageUsd: 0.134 },
  },
  // Nano Banana 2
  {
    providerCode: 'google',
    modelCode: 'gemini-3-pro-image-2',
    displayName: 'Nano Banana 2',
    section: 'image',
    modality: 'image',
    pricingUnit: 'image',
    contextLimit: 32_000,
    maxOutputTokens: 0,
    supportsStreaming: false,
    supportsVision: true,
    isActive: true,
    isAvailable: true,
    price: { inputPer1kUsd: 0.0005, outputPer1kUsd: 0, perImageUsd: 0.15 },
  },
  // GPT-Image 1 Mini
  {
    providerCode: 'openai',
    modelCode: 'gpt-image-1-mini',
    displayName: 'GPT-Image 1 Mini',
    section: 'image',
    modality: 'image',
    pricingUnit: 'image',
    contextLimit: 32_000,
    maxOutputTokens: 0,
    supportsStreaming: false,
    supportsVision: true,
    isActive: true,
    isAvailable: true,
    price: { inputPer1kUsd: 0.003, outputPer1kUsd: 0, perImageUsd: 0.02 },
  },
  // GPT-Image 1.5
  {
    providerCode: 'openai',
    modelCode: 'gpt-image-1.5',
    displayName: 'GPT-Image 1.5',
    section: 'image',
    modality: 'image',
    pricingUnit: 'image',
    contextLimit: 32_000,
    maxOutputTokens: 0,
    supportsStreaming: false,
    supportsVision: true,
    isActive: true,
    isAvailable: true,
    price: { inputPer1kUsd: 0.005, outputPer1kUsd: 0, perImageUsd: 0.04 },
  },
  // GPT-Image-2
  {
    providerCode: 'openai',
    modelCode: 'gpt-image-2',
    displayName: 'GPT-Image-2',
    section: 'image',
    modality: 'image',
    pricingUnit: 'image',
    contextLimit: 32_000,
    maxOutputTokens: 0,
    supportsStreaming: false,
    supportsVision: true,
    isActive: true,
    isAvailable: true,
    price: { inputPer1kUsd: 0.005, outputPer1kUsd: 0, perImageUsd: 0.06 },
  },
  // Grok Imagine
  {
    providerCode: 'xai',
    modelCode: 'grok-2-image',
    displayName: 'Grok Imagine',
    section: 'image',
    modality: 'image',
    pricingUnit: 'image',
    contextLimit: 8_000,
    maxOutputTokens: 0,
    supportsStreaming: false,
    supportsVision: true,
    isActive: true,
    isAvailable: true,
    price: { inputPer1kUsd: 0, outputPer1kUsd: 0, perImageUsd: 0.07 },
  },
  // Flux Pro Ultra
  {
    providerCode: 'bfl',
    modelCode: 'flux-pro-ultra',
    displayName: 'Flux Pro Ultra',
    section: 'image',
    modality: 'image',
    pricingUnit: 'image',
    contextLimit: 4_000,
    maxOutputTokens: 0,
    supportsStreaming: false,
    supportsVision: false,
    isActive: true,
    isAvailable: true,
    price: { inputPer1kUsd: 0, outputPer1kUsd: 0, perImageUsd: 0.06 },
  },
  // Flux 2.0 Pro
  {
    providerCode: 'bfl',
    modelCode: 'flux-pro-2',
    displayName: 'Flux 2.0 Pro',
    section: 'image',
    modality: 'image',
    pricingUnit: 'image',
    contextLimit: 4_000,
    maxOutputTokens: 0,
    supportsStreaming: false,
    supportsVision: true,
    isActive: true,
    isAvailable: true,
    price: { inputPer1kUsd: 0, outputPer1kUsd: 0, perImageUsd: 0.05 },
  },
  // Flux 2.0
  {
    providerCode: 'bfl',
    modelCode: 'flux-2.0',
    displayName: 'Flux 2.0',
    section: 'image',
    modality: 'image',
    pricingUnit: 'image',
    contextLimit: 4_000,
    maxOutputTokens: 0,
    supportsStreaming: false,
    supportsVision: true,
    isActive: true,
    isAvailable: true,
    price: { inputPer1kUsd: 0, outputPer1kUsd: 0, perImageUsd: 0.04 },
  },
  // Flux 2.0 Turbo
  {
    providerCode: 'bfl',
    modelCode: 'flux-2.0-turbo',
    displayName: 'Flux 2.0 Turbo',
    section: 'image',
    modality: 'image',
    pricingUnit: 'image',
    contextLimit: 4_000,
    maxOutputTokens: 0,
    supportsStreaming: false,
    supportsVision: true,
    isActive: true,
    isAvailable: true,
    price: { inputPer1kUsd: 0, outputPer1kUsd: 0, perImageUsd: 0.035 },
  },
  // Flux 2.0 Flash
  {
    providerCode: 'bfl',
    modelCode: 'flux-2.0-flash',
    displayName: 'Flux 2.0 Flash',
    section: 'image',
    modality: 'image',
    pricingUnit: 'image',
    contextLimit: 4_000,
    maxOutputTokens: 0,
    supportsStreaming: false,
    supportsVision: true,
    isActive: true,
    isAvailable: true,
    price: { inputPer1kUsd: 0, outputPer1kUsd: 0, perImageUsd: 0.025 },
  },
  // Kling Image 3.0 Omni
  {
    providerCode: 'kling',
    modelCode: 'kling-v3-omni',
    displayName: 'Kling Image 3.0 Omni',
    section: 'image',
    modality: 'image',
    pricingUnit: 'image',
    contextLimit: 4_000,
    maxOutputTokens: 0,
    supportsStreaming: false,
    supportsVision: true,
    isActive: true,
    isAvailable: true,
    price: { inputPer1kUsd: 0, outputPer1kUsd: 0, perImageUsd: 0.05 },
  },
  // Kling Image 3.0
  {
    providerCode: 'kling',
    modelCode: 'kling-image-v3',
    displayName: 'Kling Image 3.0',
    section: 'image',
    modality: 'image',
    pricingUnit: 'image',
    contextLimit: 4_000,
    maxOutputTokens: 0,
    supportsStreaming: false,
    supportsVision: true,
    isActive: true,
    isAvailable: true,
    price: { inputPer1kUsd: 0, outputPer1kUsd: 0, perImageUsd: 0.04 },
  },
  // Seedream 5.0 Pro
  {
    providerCode: 'bytedance',
    modelCode: 'seedream-5-pro',
    displayName: 'Seedream 5.0 Pro',
    section: 'image',
    modality: 'image',
    pricingUnit: 'image',
    contextLimit: 4_000,
    maxOutputTokens: 0,
    supportsStreaming: false,
    supportsVision: true,
    isActive: true,
    isAvailable: true,
    price: { inputPer1kUsd: 0, outputPer1kUsd: 0, perImageUsd: 0.04 },
  },
  // Seedream 5.0
  {
    providerCode: 'bytedance',
    modelCode: 'seedream-5-0',
    displayName: 'Seedream 5.0',
    section: 'image',
    modality: 'image',
    pricingUnit: 'image',
    contextLimit: 4_000,
    maxOutputTokens: 0,
    supportsStreaming: false,
    supportsVision: true,
    isActive: true,
    isAvailable: true,
    price: { inputPer1kUsd: 0, outputPer1kUsd: 0, perImageUsd: 0.035 },
  },
  // Seedream 4.5
  {
    providerCode: 'bytedance',
    modelCode: 'seedream-4-5',
    displayName: 'Seedream 4.5',
    section: 'image',
    modality: 'image',
    pricingUnit: 'image',
    contextLimit: 4_000,
    maxOutputTokens: 0,
    supportsStreaming: false,
    supportsVision: true,
    isActive: true,
    isAvailable: true,
    price: { inputPer1kUsd: 0, outputPer1kUsd: 0, perImageUsd: 0.03 },
  },
  // Luma Uni-1
  {
    providerCode: 'luma',
    modelCode: 'luma-uni-1',
    displayName: 'Luma Uni-1',
    section: 'image',
    modality: 'image',
    pricingUnit: 'image',
    contextLimit: 4_000,
    maxOutputTokens: 0,
    supportsStreaming: false,
    supportsVision: true,
    isActive: true,
    isAvailable: true,
    price: { inputPer1kUsd: 0, outputPer1kUsd: 0, perImageUsd: 0.06 },
  },
  // Qwen Image 3
  {
    providerCode: 'qwen',
    modelCode: 'qwen-image-3',
    displayName: 'Qwen Image 3',
    section: 'image',
    modality: 'image',
    pricingUnit: 'image',
    contextLimit: 4_000,
    maxOutputTokens: 0,
    supportsStreaming: false,
    supportsVision: true,
    isActive: true,
    isAvailable: true,
    price: { inputPer1kUsd: 0, outputPer1kUsd: 0, perImageUsd: 0.025 },
  },
  // Reve 2.1
  {
    providerCode: 'reve',
    modelCode: 'reve-2-1',
    displayName: 'Reve 2.1',
    section: 'image',
    modality: 'image',
    pricingUnit: 'image',
    contextLimit: 4_000,
    maxOutputTokens: 0,
    supportsStreaming: false,
    supportsVision: true,
    isActive: true,
    isAvailable: true,
    price: { inputPer1kUsd: 0, outputPer1kUsd: 0, perImageUsd: 0.03 },
  },

  // ───────────────────────── 3. Video ─────────────────────────
  {
    providerCode: 'xai',
    modelCode: 'grok-1.5',
    displayName: 'Grok-1.5',
    section: 'video',
    modality: 'video',
    pricingUnit: 'second',
    contextLimit: 4_000,
    maxOutputTokens: 0,
    supportsStreaming: false,
    supportsVision: true,
    isActive: true,
    isAvailable: true,
    price: { inputPer1kUsd: 0, outputPer1kUsd: 0, perSecondUsd: 0.15 },
  },
  {
    providerCode: 'google',
    modelCode: 'veo-3.1',
    displayName: 'Veo 3.1',
    section: 'video',
    modality: 'video',
    pricingUnit: 'second',
    contextLimit: 4_000,
    maxOutputTokens: 0,
    supportsStreaming: false,
    supportsVision: true,
    isActive: true,
    isAvailable: true,
    price: { inputPer1kUsd: 0, outputPer1kUsd: 0, perSecondUsd: 0.4 },
  },
  {
    providerCode: 'xai',
    modelCode: 'grok-imagine',
    displayName: 'Grok Imagine',
    section: 'video',
    modality: 'video',
    pricingUnit: 'second',
    contextLimit: 4_000,
    maxOutputTokens: 0,
    supportsStreaming: false,
    supportsVision: true,
    isActive: true,
    isAvailable: true,
    price: { inputPer1kUsd: 0, outputPer1kUsd: 0, perSecondUsd: 0.15 },
  },
  {
    providerCode: 'google',
    modelCode: 'veo-3.1-fast',
    displayName: 'Veo 3.1 (Fast)',
    section: 'video',
    modality: 'video',
    pricingUnit: 'second',
    contextLimit: 4_000,
    maxOutputTokens: 0,
    supportsStreaming: false,
    supportsVision: true,
    isActive: true,
    isAvailable: true,
    price: { inputPer1kUsd: 0, outputPer1kUsd: 0, perSecondUsd: 0.4 },
  },
  {
    providerCode: 'google',
    modelCode: 'veo-3.1-lite',
    displayName: 'Veo 3.1 Lite',
    section: 'video',
    modality: 'video',
    pricingUnit: 'second',
    contextLimit: 4_000,
    maxOutputTokens: 0,
    supportsStreaming: false,
    supportsVision: true,
    isActive: true,
    isAvailable: true,
    price: { inputPer1kUsd: 0, outputPer1kUsd: 0, perSecondUsd: 0.32 },
  },
  {
    providerCode: 'openai',
    modelCode: 'sora-2-pro-max',
    displayName: 'Sora 2 Pro Max',
    section: 'video',
    modality: 'video',
    pricingUnit: 'second',
    contextLimit: 4_000,
    maxOutputTokens: 0,
    supportsStreaming: false,
    supportsVision: true,
    isActive: false,
    isAvailable: true,
    price: { inputPer1kUsd: 0, outputPer1kUsd: 0, perSecondUsd: 0.45 },
  },
  {
    providerCode: 'openai',
    modelCode: 'sora-2-max',
    displayName: 'Sora 2 Max',
    section: 'video',
    modality: 'video',
    pricingUnit: 'second',
    contextLimit: 4_000,
    maxOutputTokens: 0,
    supportsStreaming: false,
    supportsVision: true,
    isActive: false,
    isAvailable: true,
    price: { inputPer1kUsd: 0, outputPer1kUsd: 0, perSecondUsd: 0.4 },
  },
  {
    providerCode: 'openai',
    modelCode: 'sora-2-pro',
    displayName: 'Sora 2 Pro',
    section: 'video',
    modality: 'video',
    pricingUnit: 'second',
    contextLimit: 4_000,
    maxOutputTokens: 0,
    supportsStreaming: false,
    supportsVision: true,
    isActive: false,
    isAvailable: true,
    price: { inputPer1kUsd: 0, outputPer1kUsd: 0, perSecondUsd: 0.35 },
  },
  {
    providerCode: 'openai',
    modelCode: 'sora-2',
    displayName: 'Sora 2',
    section: 'video',
    modality: 'video',
    pricingUnit: 'second',
    contextLimit: 4_000,
    maxOutputTokens: 0,
    supportsStreaming: false,
    supportsVision: true,
    isActive: false,
    isAvailable: true,
    price: { inputPer1kUsd: 0, outputPer1kUsd: 0, perSecondUsd: 0.3 },
  },
  {
    providerCode: 'bfl',
    modelCode: 'flux-3',
    displayName: 'FLUX.3',
    section: 'video',
    modality: 'video',
    pricingUnit: 'second',
    contextLimit: 4_000,
    maxOutputTokens: 0,
    supportsStreaming: false,
    supportsVision: true,
    isActive: false,
    isAvailable: true,
    price: { inputPer1kUsd: 0, outputPer1kUsd: 0, perSecondUsd: 0.24 },
  },
  {
    providerCode: 'luma',
    modelCode: 'luma-ray-3.2',
    displayName: 'Luma Ray 3.2',
    section: 'video',
    modality: 'video',
    pricingUnit: 'second',
    contextLimit: 6_000,
    maxOutputTokens: 0,
    supportsStreaming: false,
    supportsVision: true,
    isActive: true,
    isAvailable: true,
    price: { inputPer1kUsd: 0, outputPer1kUsd: 0, perSecondUsd: 0.22 },
  },
  {
    providerCode: 'minimax',
    modelCode: 'minimax-h3',
    displayName: 'MiniMax H3 (Hailuo 3)',
    section: 'video',
    modality: 'video',
    pricingUnit: 'second',
    contextLimit: 7_000,
    maxOutputTokens: 0,
    supportsStreaming: false,
    supportsVision: true,
    isActive: true,
    isAvailable: true,
    price: { inputPer1kUsd: 0, outputPer1kUsd: 0, perSecondUsd: 0.3 },
  },
  {
    providerCode: 'minimax',
    modelCode: 'minimax-h3-max',
    displayName: 'MiniMax H3 MAX (Hailuo 3)',
    section: 'video',
    modality: 'video',
    pricingUnit: 'second',
    contextLimit: 7_000,
    maxOutputTokens: 0,
    supportsStreaming: false,
    supportsVision: true,
    isActive: true,
    isAvailable: true,
    price: { inputPer1kUsd: 0, outputPer1kUsd: 0, perSecondUsd: 0.34 },
  },
  {
    providerCode: 'minimax',
    modelCode: 'hailuo-2.3',
    displayName: 'Hailuo 2.3',
    section: 'video',
    modality: 'video',
    pricingUnit: 'second',
    contextLimit: 2_000,
    maxOutputTokens: 0,
    supportsStreaming: false,
    supportsVision: true,
    isActive: true,
    isAvailable: true,
    price: { inputPer1kUsd: 0, outputPer1kUsd: 0, perSecondUsd: 0.18 },
  },
  {
    providerCode: 'minimax',
    modelCode: 'hailuo-2.3-pro',
    displayName: 'Hailuo 2.3 Pro',
    section: 'video',
    modality: 'video',
    pricingUnit: 'second',
    contextLimit: 2_000,
    maxOutputTokens: 0,
    supportsStreaming: false,
    supportsVision: true,
    isActive: true,
    isAvailable: true,
    price: { inputPer1kUsd: 0, outputPer1kUsd: 0, perSecondUsd: 0.2 },
  },
  {
    providerCode: 'minimax',
    modelCode: 'hailuo-2.3-fast-pro',
    displayName: 'Hailuo 2.3 Fast Pro',
    section: 'video',
    modality: 'video',
    pricingUnit: 'second',
    contextLimit: 2_000,
    maxOutputTokens: 0,
    supportsStreaming: false,
    supportsVision: true,
    isActive: true,
    isAvailable: true,
    price: { inputPer1kUsd: 0, outputPer1kUsd: 0, perSecondUsd: 0.19 },
  },
  {
    providerCode: 'minimax',
    modelCode: 'hailuo-2.3-fast',
    displayName: 'Hailuo 2.3 Fast',
    section: 'video',
    modality: 'video',
    pricingUnit: 'second',
    contextLimit: 2_000,
    maxOutputTokens: 0,
    supportsStreaming: false,
    supportsVision: true,
    isActive: true,
    isAvailable: true,
    price: { inputPer1kUsd: 0, outputPer1kUsd: 0, perSecondUsd: 0.16 },
  },
  {
    providerCode: 'kling',
    modelCode: 'kling-3.0',
    displayName: 'Kling 3.0',
    section: 'video',
    modality: 'video',
    pricingUnit: 'second',
    contextLimit: 2_500,
    maxOutputTokens: 0,
    supportsStreaming: false,
    supportsVision: true,
    isActive: true,
    isAvailable: true,
    price: { inputPer1kUsd: 0, outputPer1kUsd: 0, perSecondUsd: 0.18 },
  },
  {
    providerCode: 'kling',
    modelCode: 'kling-3.0-turbo',
    displayName: 'Kling 3.0 Turbo',
    section: 'video',
    modality: 'video',
    pricingUnit: 'second',
    contextLimit: 2_500,
    maxOutputTokens: 0,
    supportsStreaming: false,
    supportsVision: true,
    isActive: true,
    isAvailable: true,
    price: { inputPer1kUsd: 0, outputPer1kUsd: 0, perSecondUsd: 0.14 },
  },
  {
    providerCode: 'bytedance',
    modelCode: 'seedance-2.5',
    displayName: 'Seedance 2.5',
    section: 'video',
    modality: 'video',
    pricingUnit: 'second',
    contextLimit: 10_000,
    maxOutputTokens: 0,
    supportsStreaming: false,
    supportsVision: true,
    isActive: true,
    isAvailable: true,
    price: { inputPer1kUsd: 0, outputPer1kUsd: 0, perSecondUsd: 0.3 },
  },
  {
    providerCode: 'bytedance',
    modelCode: 'seedance-2.0-mini',
    displayName: 'Seedance 2.0 Mini',
    section: 'video',
    modality: 'video',
    pricingUnit: 'second',
    contextLimit: 4_000,
    maxOutputTokens: 0,
    supportsStreaming: false,
    supportsVision: true,
    isActive: true,
    isAvailable: true,
    price: { inputPer1kUsd: 0, outputPer1kUsd: 0, perSecondUsd: 0.16 },
  },
  {
    providerCode: 'bytedance',
    modelCode: 'seedance-2.0',
    displayName: 'Seedance 2.0',
    section: 'video',
    modality: 'video',
    pricingUnit: 'second',
    contextLimit: 4_000,
    maxOutputTokens: 0,
    supportsStreaming: false,
    supportsVision: true,
    isActive: true,
    isAvailable: true,
    price: { inputPer1kUsd: 0, outputPer1kUsd: 0, perSecondUsd: 0.2 },
  },
  {
    providerCode: 'bytedance',
    modelCode: 'seedance-2.0-fast',
    displayName: 'Seedance 2.0 Fast',
    section: 'video',
    modality: 'video',
    pricingUnit: 'second',
    contextLimit: 4_000,
    maxOutputTokens: 0,
    supportsStreaming: false,
    supportsVision: true,
    isActive: true,
    isAvailable: true,
    price: { inputPer1kUsd: 0, outputPer1kUsd: 0, perSecondUsd: 0.16 },
  },
  {
    providerCode: 'google',
    modelCode: 'gemini-omni-flash-1.1',
    displayName: 'Gemini Omni Flash 1.1',
    section: 'video',
    modality: 'video',
    pricingUnit: 'second',
    contextLimit: 4_000,
    maxOutputTokens: 0,
    supportsStreaming: false,
    supportsVision: true,
    isActive: true,
    isAvailable: true,
    price: { inputPer1kUsd: 0, outputPer1kUsd: 0, perSecondUsd: 0.1 },
  },
  {
    providerCode: 'qwen',
    modelCode: 'wan-3.0',
    displayName: 'Wan 3.0',
    section: 'video',
    modality: 'video',
    pricingUnit: 'second',
    contextLimit: 20_000,
    maxOutputTokens: 0,
    supportsStreaming: false,
    supportsVision: true,
    isActive: true,
    isAvailable: true,
    price: { inputPer1kUsd: 0, outputPer1kUsd: 0, perSecondUsd: 0.2 },
  },
  {
    providerCode: 'qwen',
    modelCode: 'wan-3.0-prime',
    displayName: 'Wan 3.0 Prime',
    section: 'video',
    modality: 'video',
    pricingUnit: 'second',
    contextLimit: 20_000,
    maxOutputTokens: 0,
    supportsStreaming: false,
    supportsVision: true,
    isActive: true,
    isAvailable: true,
    price: { inputPer1kUsd: 0, outputPer1kUsd: 0, perSecondUsd: 0.24 },
  },
  {
    providerCode: 'qwen',
    modelCode: 'wan-2.7',
    displayName: 'Wan 2.7',
    section: 'video',
    modality: 'video',
    pricingUnit: 'second',
    contextLimit: 2_000,
    maxOutputTokens: 0,
    supportsStreaming: false,
    supportsVision: true,
    isActive: true,
    isAvailable: true,
    price: { inputPer1kUsd: 0, outputPer1kUsd: 0, perSecondUsd: 0.16 },
  },
  {
    providerCode: 'qwen',
    modelCode: 'wan-2.6',
    displayName: 'Wan 2.6',
    section: 'video',
    modality: 'video',
    pricingUnit: 'second',
    contextLimit: 20_000,
    maxOutputTokens: 0,
    supportsStreaming: false,
    supportsVision: true,
    isActive: true,
    isAvailable: true,
    price: { inputPer1kUsd: 0, outputPer1kUsd: 0, perSecondUsd: 0.14 },
  },
  {
    providerCode: 'kling',
    modelCode: 'kling-3.0-omni-edit',
    displayName: 'Kling 3.0 Omni Edit',
    section: 'video',
    modality: 'video',
    pricingUnit: 'second',
    contextLimit: 2_500,
    maxOutputTokens: 0,
    supportsStreaming: false,
    supportsVision: true,
    isActive: true,
    isAvailable: true,
    price: { inputPer1kUsd: 0, outputPer1kUsd: 0, perSecondUsd: 0.2 },
  },
  {
    providerCode: 'kling',
    modelCode: 'kling-o1-video-edit',
    displayName: 'Kling O1 Video Edit',
    section: 'video',
    modality: 'video',
    pricingUnit: 'second',
    contextLimit: 2_500,
    maxOutputTokens: 0,
    supportsStreaming: false,
    supportsVision: true,
    isActive: true,
    isAvailable: true,
    price: { inputPer1kUsd: 0, outputPer1kUsd: 0, perSecondUsd: 0.18 },
  },
  {
    providerCode: 'bfl',
    modelCode: 'flux-3-video-edit',
    displayName: 'FLUX.3 Video Edit',
    section: 'video',
    modality: 'video',
    pricingUnit: 'second',
    contextLimit: 4_000,
    maxOutputTokens: 0,
    supportsStreaming: false,
    supportsVision: true,
    isActive: false,
    isAvailable: true,
    price: { inputPer1kUsd: 0, outputPer1kUsd: 0, perSecondUsd: 0.2 },
  },
  {
    providerCode: 'xai',
    modelCode: 'grok-imagine-edit',
    displayName: 'Grok Imagine Edit',
    section: 'video',
    modality: 'video',
    pricingUnit: 'second',
    contextLimit: 3_000,
    maxOutputTokens: 0,
    supportsStreaming: false,
    supportsVision: true,
    isActive: true,
    isAvailable: true,
    price: { inputPer1kUsd: 0, outputPer1kUsd: 0, perSecondUsd: 0.18 },
  },
  {
    providerCode: 'google',
    modelCode: 'gemini-omni-flash-1.1-edit',
    displayName: 'Gemini Omni Flash 1.1 Edit',
    section: 'video',
    modality: 'video',
    pricingUnit: 'second',
    contextLimit: 4_000,
    maxOutputTokens: 0,
    supportsStreaming: false,
    supportsVision: true,
    isActive: true,
    isAvailable: true,
    price: { inputPer1kUsd: 0, outputPer1kUsd: 0, perSecondUsd: 0.12 },
  },
  {
    providerCode: 'bytedance',
    modelCode: 'seedance-2.5-edit',
    displayName: 'Seedance 2.5 Edit',
    section: 'video',
    modality: 'video',
    pricingUnit: 'second',
    contextLimit: 4_000,
    maxOutputTokens: 0,
    supportsStreaming: false,
    supportsVision: true,
    isActive: true,
    isAvailable: true,
    price: { inputPer1kUsd: 0, outputPer1kUsd: 0, perSecondUsd: 0.28 },
  },
  {
    providerCode: 'google',
    modelCode: 'gemini-omni-flash-1.1-extend',
    displayName: 'Gemini Omni Flash 1.1 Extend',
    section: 'video',
    modality: 'video',
    pricingUnit: 'second',
    contextLimit: 4_000,
    maxOutputTokens: 0,
    supportsStreaming: false,
    supportsVision: true,
    isActive: true,
    isAvailable: true,
    price: { inputPer1kUsd: 0, outputPer1kUsd: 0, perSecondUsd: 0.1 },
  },
  {
    providerCode: 'google',
    modelCode: 'veo-3.1-extend',
    displayName: 'Veo 3.1 Extend',
    section: 'video',
    modality: 'video',
    pricingUnit: 'second',
    contextLimit: 4_000,
    maxOutputTokens: 0,
    supportsStreaming: false,
    supportsVision: true,
    isActive: true,
    isAvailable: true,
    price: { inputPer1kUsd: 0, outputPer1kUsd: 0, perSecondUsd: 0.4 },
  },
  {
    providerCode: 'kling',
    modelCode: 'kling-3.0-motion-control',
    displayName: 'Kling 3.0 Motion Control',
    section: 'video',
    modality: 'video',
    pricingUnit: 'second',
    contextLimit: 4_000,
    maxOutputTokens: 0,
    supportsStreaming: false,
    supportsVision: true,
    isActive: true,
    isAvailable: true,
    price: { inputPer1kUsd: 0, outputPer1kUsd: 0, perSecondUsd: 0.14 },
  },
  {
    providerCode: 'kling',
    modelCode: 'kling-motion-control',
    displayName: 'Kling Motion Control',
    section: 'video',
    modality: 'video',
    pricingUnit: 'second',
    contextLimit: 4_000,
    maxOutputTokens: 0,
    supportsStreaming: false,
    supportsVision: true,
    isActive: true,
    isAvailable: true,
    price: { inputPer1kUsd: 0, outputPer1kUsd: 0, perSecondUsd: 0 },
  },
  {
    providerCode: 'kling',
    modelCode: 'marketing_studio_video',
    displayName: 'Marketing Studio Video',
    section: 'video2',
    modality: 'video',
    pricingUnit: 'second',
    contextLimit: 4_000,
    maxOutputTokens: 0,
    supportsStreaming: false,
    supportsVision: true,
    isActive: true,
    isAvailable: true,
    price: { inputPer1kUsd: 0, outputPer1kUsd: 0, perSecondUsd: 0.2 },
  },

  // ───────────────────────── 4. Character / identity ─────────────────────────
  {
    providerCode: 'google',
    modelCode: 'soul-id-studio',
    displayName: 'Soul ID Studio',
    section: 'character',
    modality: 'image',
    pricingUnit: 'request',
    contextLimit: 2_000,
    maxOutputTokens: 0,
    supportsStreaming: false,
    supportsVision: true,
    isActive: true,
    isAvailable: true,
    price: { inputPer1kUsd: 0, outputPer1kUsd: 0, perRequestUsd: 0.12 },
  },
  {
    providerCode: 'google',
    modelCode: 'soul-cast-image',
    displayName: 'Soul Cast Image',
    section: 'character',
    modality: 'image',
    pricingUnit: 'image',
    contextLimit: 2_000,
    maxOutputTokens: 0,
    supportsStreaming: false,
    supportsVision: true,
    isActive: true,
    isAvailable: true,
    price: { inputPer1kUsd: 0, outputPer1kUsd: 0, perImageUsd: 0.05 },
  },
  {
    // One request = a Gemini reference frame (Soul ID Studio) + a textured Meshy
    // image-to-3D build, so the price covers both steps.
    providerCode: 'meshy',
    modelCode: 'soul-3d',
    displayName: 'Soul 3D Character',
    section: 'character',
    modality: 'model3d',
    pricingUnit: 'request',
    contextLimit: 2_000,
    maxOutputTokens: 0,
    supportsStreaming: false,
    supportsVision: true,
    isActive: true,
    isAvailable: true,
    price: { inputPer1kUsd: 0, outputPer1kUsd: 0, perRequestUsd: 0.75 },
  },

  // ───────────────────────── 5. Audio / music generation ─────────────────────────
  {
    providerCode: 'google',
    modelCode: 'seed-audio-1-0',
    displayName: 'Seed Audio 1.0',
    section: 'audio',
    modality: 'audio',
    pricingUnit: 'second',
    contextLimit: 2_000,
    maxOutputTokens: 0,
    supportsStreaming: false,
    supportsVision: false,
    isActive: true,
    isAvailable: true,
    price: { inputPer1kUsd: 0, outputPer1kUsd: 0, perSecondUsd: 0.008 },
  },
  {
    providerCode: 'suno',
    modelCode: 'suno-v5',
    displayName: 'Suno V5',
    section: 'audio',
    modality: 'audio',
    pricingUnit: 'request',
    contextLimit: 2_000,
    maxOutputTokens: 0,
    supportsStreaming: false,
    supportsVision: false,
    isActive: true,
    isAvailable: true,
    price: { inputPer1kUsd: 0, outputPer1kUsd: 0, perRequestUsd: 0.1 },
  },
  {
    providerCode: 'udio',
    modelCode: 'udio-v1.5',
    displayName: 'Udio 1.5',
    section: 'audio',
    modality: 'audio',
    pricingUnit: 'request',
    contextLimit: 2_000,
    maxOutputTokens: 0,
    supportsStreaming: false,
    supportsVision: false,
    isActive: true,
    isAvailable: true,
    price: { inputPer1kUsd: 0, outputPer1kUsd: 0, perRequestUsd: 0.08 },
  },
  {
    providerCode: 'aiva',
    modelCode: 'aiva-v3',
    displayName: 'AIVA v3',
    section: 'audio',
    modality: 'audio',
    pricingUnit: 'request',
    contextLimit: 2_000,
    maxOutputTokens: 0,
    supportsStreaming: false,
    supportsVision: false,
    isActive: true,
    isAvailable: true,
    price: { inputPer1kUsd: 0, outputPer1kUsd: 0, perRequestUsd: 0.05 },
  },
  {
    providerCode: 'elevenlabs',
    modelCode: 'eleven-music-v1',
    displayName: 'ElevenLabs Music',
    section: 'audio',
    modality: 'audio',
    pricingUnit: 'second',
    contextLimit: 2_000,
    maxOutputTokens: 0,
    supportsStreaming: false,
    supportsVision: false,
    isActive: true,
    isAvailable: true,
    price: { inputPer1kUsd: 0, outputPer1kUsd: 0, perSecondUsd: 0.01 },
  },
  {
    providerCode: 'google',
    modelCode: 'lyria-002',
    displayName: 'Google Lyria (MusicLM)',
    section: 'audio',
    modality: 'audio',
    pricingUnit: 'second',
    contextLimit: 2_000,
    maxOutputTokens: 0,
    supportsStreaming: false,
    supportsVision: false,
    isActive: true,
    isAvailable: true,
    price: { inputPer1kUsd: 0, outputPer1kUsd: 0, perSecondUsd: 0.006 },
  },

  // ───────────────────────── 6. Voice (TTS / STT / cloning) ─────────────────────────
  {
    providerCode: 'elevenlabs',
    modelCode: 'eleven-multilingual-v2',
    displayName: 'ElevenLabs Multilingual v2',
    section: 'voice',
    modality: 'speech',
    pricingUnit: 'character',
    contextLimit: 5_000,
    maxOutputTokens: 0,
    supportsStreaming: true,
    supportsVision: false,
    isActive: true,
    isAvailable: true,
    price: { inputPer1kUsd: 0, outputPer1kUsd: 0, perCharUsd: 0.18 },
  },
  {
    providerCode: 'openai',
    modelCode: 'whisper-1',
    displayName: 'OpenAI Whisper (STT)',
    section: 'voice',
    modality: 'transcription',
    pricingUnit: 'minute',
    contextLimit: 0,
    maxOutputTokens: 0,
    supportsStreaming: false,
    supportsVision: false,
    isActive: true,
    isAvailable: true,
    price: { inputPer1kUsd: 0, outputPer1kUsd: 0, perMinuteUsd: 0.006 },
  },
  {
    providerCode: 'openai',
    modelCode: 'gpt-4o-mini-transcribe',
    displayName: 'OpenAI GPT-4o mini Transcribe (STT)',
    section: 'voice',
    modality: 'transcription',
    pricingUnit: 'minute',
    contextLimit: 0,
    maxOutputTokens: 0,
    supportsStreaming: false,
    supportsVision: false,
    isActive: true,
    isAvailable: true,
    price: { inputPer1kUsd: 0, outputPer1kUsd: 0, perMinuteUsd: 0.003 },
  },
  {
    providerCode: 'openai',
    modelCode: 'gpt-4o-mini-tts',
    displayName: 'GPT Voice (TTS)',
    section: 'voice',
    modality: 'speech',
    pricingUnit: 'character',
    contextLimit: 5_000,
    maxOutputTokens: 0,
    supportsStreaming: true,
    supportsVision: false,
    isActive: true,
    isAvailable: true,
    price: { inputPer1kUsd: 0, outputPer1kUsd: 0, perCharUsd: 0.015 },
  },
  {
    providerCode: 'google',
    modelCode: 'gemini-3.1-flash-tts',
    displayName: 'Gemini 3.1 Flash TTS',
    section: 'voice',
    modality: 'speech',
    pricingUnit: 'character',
    contextLimit: 5_000,
    maxOutputTokens: 0,
    supportsStreaming: true,
    supportsVision: false,
    isActive: true,
    isAvailable: true,
    price: { inputPer1kUsd: 0, outputPer1kUsd: 0, perCharUsd: 0.01 },
  },
  {
    providerCode: 'hume',
    modelCode: 'octave-tts',
    displayName: 'Hume AI Octave',
    section: 'voice',
    modality: 'speech',
    pricingUnit: 'character',
    contextLimit: 5_000,
    maxOutputTokens: 0,
    supportsStreaming: true,
    supportsVision: false,
    isActive: true,
    isAvailable: true,
    price: { inputPer1kUsd: 0, outputPer1kUsd: 0, perCharUsd: 0.2 },
  },
  {
    providerCode: 'suno',
    modelCode: 'suno-voice',
    displayName: 'Suno Voice',
    section: 'voice',
    modality: 'speech',
    pricingUnit: 'request',
    contextLimit: 5_000,
    maxOutputTokens: 0,
    supportsStreaming: false,
    supportsVision: false,
    isActive: true,
    isAvailable: true,
    price: { inputPer1kUsd: 0, outputPer1kUsd: 0, perRequestUsd: 0.06 },
  },
  {
    providerCode: 'murf',
    modelCode: 'murf-gen2',
    displayName: 'Murf Gen2',
    section: 'voice',
    modality: 'speech',
    pricingUnit: 'character',
    contextLimit: 5_000,
    maxOutputTokens: 0,
    supportsStreaming: false,
    supportsVision: false,
    isActive: true,
    isAvailable: true,
    price: { inputPer1kUsd: 0, outputPer1kUsd: 0, perCharUsd: 0.05 },
  },

  // ───────────────────────── 7. Design & UI/UX ─────────────────────────
  // Most design tools have no public generation API — they are offered as a
  // catalogue with deep links; those that do get a normal adapter later.
  ...(
    [
      ['figma-make', 'Figma AI / Figma Make', 'https://www.figma.com/make/'],
      ['adobe-firefly', 'Adobe Firefly + Photoshop AI', 'https://firefly.adobe.com/'],
      ['framer-ai', 'Framer AI', 'https://www.framer.com/ai/'],
      ['galileo-ai', 'Galileo AI', 'https://www.usegalileo.ai/'],
      ['uizard', 'Uizard', 'https://uizard.io/'],
      ['canva-magic', 'Canva Magic Studio', 'https://www.canva.com/magic-studio/'],
      ['google-stitch', 'Google Stitch', 'https://stitch.withgoogle.com/'],
      ['relume', 'Relume', 'https://www.relume.io/'],
    ] as const
  ).map<ModelSeed>(([code, name, url]) => ({
    providerCode: 'design-tools',
    modelCode: code,
    displayName: name,
    section: 'design',
    modality: 'design',
    pricingUnit: 'request',
    contextLimit: 0,
    maxOutputTokens: 0,
    supportsStreaming: false,
    supportsVision: false,
    isActive: true,
    isAvailable: true,
    price: { inputPer1kUsd: 0, outputPer1kUsd: 0, perRequestUsd: 0 },
    externalUrl: url,
  })),
];

export const MODEL_CATALOG: AIModel[] = seeds.map((seed) => ({
  ...seed,
  id: modelId(seed.providerCode, seed.modelCode),
  reasoningSummarySupport:
    seed.reasoningSummarySupport ?? resolveReasoningSummarySupport(seed.providerCode, seed.modelCode),
  lastSeenAt: seed.lastSeenAt ?? null,
  priceConfirmedAt: seed.priceConfirmedAt ?? null,
}));

export function findCatalogModel(id: string): AIModel | undefined {
  return MODEL_CATALOG.find((model) => model.id === id);
}
